import React from 'react';
import {Link} from 'react-router-dom';

import {CopyButton} from 'src/components/copy-button/copy-button';
import {DataType} from 'src/components/data-types/data-type';
import {PossibleValues} from 'src/components/possible-values/possible-values';
import {RemoveAttribute} from 'src/components/remove-attribute/remove-attribute';
import {useUserRoles} from 'src/hooks/useUserRoles';
import {createAttributeRoutePath} from 'src/modules/attributes/attributes-route-planner';
import {ColumnTypes, ColumnWidth} from 'src/modules/event/constants';
import {FieldName} from 'src/modules/event-create/typedefs';
import {EventDetailInfo} from 'src/openapi/models/EventDetailInfo';
import {Role} from 'src/openapi/models/Role';
import {getPossibleValuesOrDefault} from 'src/utils/getPossibleValuesOrDefault';

import {CheckBox} from '@pb/kit-component-checkbox';
import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import {
    HorizontalPositionElement,
    PushNotification,
    VerticalPositionElement,
} from '@pb/kit-component-push-notification';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import {formatDate} from '@pb/kit-formatters';

type Props = {
    eventDetailInfo: EventDetailInfo,
};

const NO_ATTRIBUTE_USAGE = 'No Usage';
const NOTIFICATION_CONFIG = {
    horizontal: HorizontalPositionElement.RIGHT,
    vertical: VerticalPositionElement.BOTTOM,
};

export const Attributes = ({eventDetailInfo}: Props) => {
    const userRoles = useUserRoles();

    return (
        <Group direction="v" gap="sm">
            <strong>
                Attributes:
            </strong>
            <Table borderedGrid={true}>
                <Head>
                    <Cell width={ColumnWidth[ColumnTypes.ATTRIBUTE_NAME]}>Attribute</Cell>
                    <Cell width={ColumnWidth[ColumnTypes.ATTRIBUTE_DESCRIPTION]}>
                        Attribute Description
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.DATA_TYPE]}>Data Type</Cell>
                    <Cell width={ColumnWidth[ColumnTypes.POSSIBLE_VALUES]}>Possible Values</Cell>
                    <Cell width={ColumnWidth[ColumnTypes.REQUIRED]}>Required</Cell>
                    <Cell width={ColumnWidth[ColumnTypes.DEPRECATED]}>Deprecated</Cell>
                    <Cell width={ColumnWidth[ColumnTypes.ATTRIBUTE_LAST_USAGE_DATE]}>Last Usage Date</Cell>
                </Head>
                <Body>
                    {eventDetailInfo.attributes.map(attribute => (
                        <Row key={attribute.name}>
                            <Cell>
                                <Group gap="s" justify="space-between" alignItems="center">
                                    <EllipsisTextHint>
                                        <Link to={createAttributeRoutePath({
                                            attributeName: attribute.name,
                                            attributeId: attribute.id,
                                        })}>
                                            {attribute.name}
                                        </Link>
                                    </EllipsisTextHint>
                                    {userRoles.includes(Role.ROLE_UDEDT_ADMIN) && !attribute.lastUsageDate && (
                                        <Item style={{width: '1rem'}}>
                                            <PushNotification anchorOrigin={NOTIFICATION_CONFIG}>
                                                <RemoveAttribute attributeDetailInfo={attribute}/>
                                            </PushNotification>
                                        </Item>
                                    )}
                                </Group>
                            </Cell>
                            <Cell title={true}>
                                {attribute.description}
                            </Cell>
                            <Cell title={true}>
                                <DataType
                                    dataType={attribute.fieldType}
                                    itemsType={attribute?.itemsType}
                                />
                            </Cell>
                            <Cell>
                                <Group gap="s" justify="space-between" alignItems="center">
                                    <EllipsisTextHint>
                                        <PossibleValues possibleValues={attribute.possibleValues}/>
                                    </EllipsisTextHint>
                                    <CopyButton textToCopy={getPossibleValuesOrDefault(attribute.possibleValues)}/>
                                </Group>
                            </Cell>
                            <Cell verticalAlign="middle">
                                <Group justify="center">
                                    <CheckBox
                                        name={FieldName.REQUIRED}
                                        checked={attribute.required}
                                        disabled={true}
                                    />
                                </Group>
                            </Cell>
                            <Cell verticalAlign="middle">
                                <Group justify="center">
                                    <CheckBox
                                        name={FieldName.DEPRECATED}
                                        checked={attribute.deprecated}
                                        disabled={true}
                                    />
                                </Group>
                            </Cell>
                            <Cell verticalAlign="middle">
                                <Group justify="center">
                                    {attribute.lastUsageDate
                                        ? formatDate(attribute.lastUsageDate)
                                        : NO_ATTRIBUTE_USAGE}
                                </Group>
                            </Cell>
                        </Row>
                    ))}
                </Body>
            </Table>
        </Group>
    );
};
